<script setup lang="ts">
import { ref, onMounted } from "vue";

import { v4 as uuidv4 } from "uuid";

import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

const props = defineProps<{
  disabled?: boolean;
  label?: string;
  bigLabel?: boolean;
  color: "primary" | "positive" | "warning" | "danger" | "disabled";
  value: boolean;
  infoContent?: string;
  /**
   * Accessible name for a switch whose visible text lives OUTSIDE this component — a table row
   * whose label is a sibling cell, for instance. Without one of `label` / `ariaLabel`, a screen
   * reader announces a bare unnamed switch.
   */
  ariaLabel?: string;
}>();

const value = ref<boolean>(props.value);
const id = ref<string>(uuidv4());

const emits = defineEmits(["update:value"]);

const updateInput = () => {
  value.value = !value.value;
  emits("update:value", value.value);
};

onMounted(() => {
  if (value.value) {
    value.value = !value.value;
    const label = document.getElementById(id.value);
    label?.click();
  }
});
</script>

<template>
  <div class="flex items-center justify-between gap-3">
    <label
      v-if="label"
      :for="id"
      :class="[bigLabel ? '' : 't-xs text-type-muted']"
      class="flex gap-1"
      >{{ label }}
    </label>

    <div class="flex">
      <label
        :for="id"
        class="relative inline-flex items-center rounded-full transition-all"
        :class="[props.disabled ? 'cursor-default' : 'cursor-pointer']">
        <input
          :id="id"
          type="checkbox"
          role="switch"
          :value="value"
          :disabled="props.disabled"
          :aria-label="ariaLabel"
          class="peer sr-only"
          @input="updateInput" />
        <div
          data-cy="checkbox"
          class="peer-focus-visible:outline-app-blue-600 after:bg-slate-0 duration-fast ease-standard after:duration-fast after:ease-standard after:shadow-card h-6 w-11 rounded-full bg-slate-300 transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-full"
          :class="{
            'peer-checked:bg-disabled': props.color === 'disabled',
            'peer-checked:bg-app-green-400': props.color === 'positive',
            'peer-checked:bg-app-yellow-400': props.color === 'warning',
            'peer-checked:bg-app-red-200': props.color === 'danger',
            'peer-checked:bg-app-blue-600': props.color === 'primary',
          }"></div>
      </label>
    </div>
    <InfoIcon
      v-if="infoContent"
      :tooltip="infoContent" />
  </div>
</template>
